import { useAuth } from '../AuthContext';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import CaricaDocumenti from './CaricaDocumenti';
import { CheckCircle, Loader } from 'lucide-react';
import API from '../api';

// ── Validazione CF ────────────────────────────────────────────────────────────
function validaCF(cf) {
  if (!cf) return false;
  cf = cf.toUpperCase().trim();
  if (cf.length !== 16) return false;
  const regex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
  return regex.test(cf);
}

// ── Algoritmo Codice Fiscale (decodifica / codifica) ───────────────────────────
const CF_MESI = ['A','B','C','D','E','H','L','M','P','R','S','T'];

function decodificaCF(cf) {
  if (!validaCF(cf)) return null;
  cf = cf.toUpperCase().trim();
  const meseChar = cf[8];
  const meseIdx = CF_MESI.indexOf(meseChar);
  if (meseIdx < 0) return null;
  let giorno = parseInt(cf.substring(9, 11), 10);
  let sesso = 'M';
  if (giorno > 40) { sesso = 'F'; giorno -= 40; }
  if (giorno < 1 || giorno > 31) return null;
  const anno2 = parseInt(cf.substring(6, 8), 10);
  const codiceBelfiore = cf.substring(11, 15);
  return { sesso, giorno, mese: meseIdx + 1, anno2, codiceBelfiore };
}

function annoCompletoDaCF(anno2, oggi = new Date()) {
  // Non c'e' modo certo di sapere il secolo dal solo CF: si assume che la
  // persona non abbia piu' di ~115 anni e non sia nata nel futuro.
  const annoCorrente = oggi.getFullYear();
  const secolo2000 = 2000 + anno2;
  const secolo1900 = 1900 + anno2;
  return secolo2000 <= annoCorrente ? secolo2000 : secolo1900;
}

function consonantiVocali(s) {
  s = (s || '').toUpperCase().replace(/[^A-Z]/g, '');
  const cons = s.split('').filter(c => !'AEIOU'.includes(c));
  const voc = s.split('').filter(c => 'AEIOU'.includes(c));
  return (cons.join('') + voc.join('') + 'XXX').substring(0, 3);
}

function codiceCognome(cognome) {
  return consonantiVocali(cognome);
}

function codiceNome(nome) {
  nome = (nome || '').toUpperCase().replace(/[^A-Z]/g, '');
  const cons = nome.split('').filter(c => !'AEIOU'.includes(c));
  if (cons.length >= 4) {
    return (cons[0] + cons[2] + cons[3]);
  }
  return consonantiVocali(nome);
}

const CF_PESI_DISPARI = {
  '0':1,'1':0,'2':5,'3':7,'4':9,'5':13,'6':15,'7':17,'8':19,'9':21,
  'A':1,'B':0,'C':5,'D':7,'E':9,'F':13,'G':15,'H':17,'I':19,'J':21,
  'K':2,'L':4,'M':18,'N':20,'O':11,'P':3,'Q':6,'R':8,'S':12,'T':14,
  'U':16,'V':10,'W':22,'X':25,'Y':24,'Z':23,
};
const CF_PESI_PARI = {
  '0':0,'1':1,'2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,
  'A':0,'B':1,'C':2,'D':3,'E':4,'F':5,'G':6,'H':7,'I':8,'J':9,
  'K':10,'L':11,'M':12,'N':13,'O':14,'P':15,'Q':16,'R':17,'S':18,'T':19,
  'U':20,'V':21,'W':22,'X':23,'Y':24,'Z':25,
};
const CF_RESTO_LETTERA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function carattereControlloCF(quindici) {
  let somma = 0;
  for (let i = 0; i < 15; i++) {
    const ch = quindici[i];
    somma += (i % 2 === 0) ? CF_PESI_DISPARI[ch] : CF_PESI_PARI[ch];
  }
  return CF_RESTO_LETTERA[somma % 26];
}

// Genera il CF (base, senza gestione omocodie) da cognome, nome, sesso,
// data di nascita (Date) e codice belfiore del luogo di nascita.
function codificaCF(cognome, nome, sesso, dataNascita, codiceBelfiore) {
  if (!cognome || !nome || !sesso || !dataNascita || !codiceBelfiore) return null;
  const cCognome = codiceCognome(cognome);
  const cNome = codiceNome(nome);
  const anno2 = String(dataNascita.getFullYear()).slice(-2);
  const meseChar = CF_MESI[dataNascita.getMonth()];
  let giorno = dataNascita.getDate();
  if (sesso === 'F') giorno += 40;
  const giornoStr = String(giorno).padStart(2, '0');
  const quindici = `${cCognome}${cNome}${anno2}${meseChar}${giornoStr}${codiceBelfiore.toUpperCase()}`;
  const controllo = carattereControlloCF(quindici);
  return quindici + controllo;
}

// ── Validazione PI ────────────────────────────────────────────────────────────
function validaPI(pi) {
  if (!pi) return false;
  pi = pi.trim();
  if (!/^\d{11}$/.test(pi)) return false;
  let s = 0;
  for (let i = 0; i < 11; i++) {
    let n = parseInt(pi[i]);
    if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
    s += n;
  }
  return s % 10 === 0;
}

const LabelInput = ({ label, children, errore }) => (
  <div>
    <div style={{ fontSize: 11, color: '#718096', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    {children}
    {errore && <div style={{ fontSize: 11, color: '#C53030', marginTop: 2 }}>{errore}</div>}
  </div>
);

const Campo = ({ label, value }) => {
  if (value === null || value === undefined || value === '' || value === '—') return null;
  return (
  <div>
    <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#1A1A2E', padding: '5px 8px', background: '#F7F8FA',
      borderRadius: 5, border: '1px solid #edf2f7' }}>{value}</div>
  </div>
  );
};

const Sez = ({ titolo, colore }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: colore, textTransform: 'uppercase',
    letterSpacing: '0.8px', marginBottom: 10, paddingBottom: 6,
    borderBottom: `2px solid ${colore}20`, marginTop: 16 }}>{titolo}</div>
);

// ── Vista sola lettura ────────────────────────────────────────────────────────
function VistaCliente({ cliente, onModifica }) {
  const isAzienda = cliente.tipo_cliente === 'azienda';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={onModifica}
          style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #718096',
            background: 'white', cursor: 'pointer', fontSize: 11, color: '#718096' }}>
          ✏️ Modifica anagrafica
        </button>
      </div>

      <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
        background: isAzienda ? '#EBF4FF' : '#E2EFDA', color: isAzienda ? '#2E75B6' : '#276749',
        marginBottom: 12 }}>
        {isAzienda ? '🏢 Azienda' : '👤 Privato'}
        {isAzienda && cliente.pubblica_amministrazione && <span style={{ marginLeft: 6 }}>— P.A.</span>}
      </div>

      {isAzienda ? (
        <>
          <Sez titolo="Azienda" colore="#2E75B6" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <Campo label="Ragione sociale" value={cliente.ragione_sociale} />
            <Campo label="Partita IVA" value={cliente.partita_iva} />
            <Campo label="Pubblica amministrazione" value={cliente.pubblica_amministrazione ? 'Sì' : 'No'} />
            <Campo label="Cognome referente" value={cliente.cognome_referente} />
            <Campo label="Nome referente" value={cliente.nome_referente} />
          </div>
        </>
      ) : (
        <>
          <Sez titolo="Dati anagrafici" colore="#2E75B6" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <Campo label="Cognome" value={cliente.cognome} />
            <Campo label="Nome" value={cliente.nome} />
            <Campo label="Codice fiscale" value={cliente.codice_fiscale} />
            <Campo label="Convenzione" value={cliente.convenzione} />
          </div>
        </>
      )}

      <Sez titolo="Recapiti" colore="#E67E22" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        <Campo label="Indirizzo" value={cliente.indirizzo} />
        <Campo label="CAP" value={cliente.cap} />
        <Campo label="Comune" value={cliente.localita} />
        <Campo label="Provincia" value={cliente.provincia} />
        <Campo label="Telefono 1" value={cliente.telefono} />
        <Campo label="Telefono 2" value={cliente.telefono2} />
        <Campo label="Email" value={cliente.email} />
        {isAzienda && <Campo label="PEC" value={cliente.pec} />}
        {isAzienda && <Campo label="Codice SDI" value={cliente.codice_sdi} />}
      </div>

      {cliente.note && (
        <>
          <Sez titolo="Note" colore="#718096" />
          <div style={{ fontSize: 13, color: '#1A1A2E', padding: '8px 10px',
            background: '#F7F8FA', borderRadius: 6, border: '1px solid #edf2f7' }}>{cliente.note}</div>
        </>
      )}

      <CaricaDocumenti tipo="cliente" id={cliente.codice_cliente} titolo="Documenti anagrafici" />
    </div>
  );
}

// ── Form modifica ─────────────────────────────────────────────────────────────
function FormCliente({ cliente, onSave, onCancel, onElimina }) {
  const [form, setForm] = useState({
    tipo_cliente: cliente.tipo_cliente || 'privato',
    cognome: cliente.cognome || '',
    nome: cliente.nome || '',
    codice_fiscale: cliente.codice_fiscale || '',
    data_nascita: cliente.data_nascita || '',
    luogo_nascita: cliente.luogo_nascita || '',
    provincia_nascita: cliente.provincia_nascita || '',
    sesso: cliente.sesso || '',
    convenzione: cliente.convenzione || '',
    ragione_sociale: cliente.ragione_sociale || '',
    cognome_referente: cliente.cognome_referente || '',
    nome_referente: cliente.nome_referente || '',
    partita_iva: cliente.partita_iva || '',
    pubblica_amministrazione: cliente.pubblica_amministrazione || false,
    indirizzo: cliente.indirizzo || '',
    cap: cliente.cap || '',
    localita: cliente.localita || '',
    provincia: cliente.provincia || '',
    telefono: cliente.telefono || '',
    telefono2: cliente.telefono2 || '',
    email: cliente.email || '',
    pec: cliente.pec || '',
    codice_sdi: cliente.codice_sdi || '',
    note: cliente.note || ''
  });
  const [errori, setErrori] = useState({});
  const [carico, setCarico] = useState(false);

  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
  const [risultatiComuneCap, setRisultatiComuneCap] = useState([]);
  const [capOpzioni, setCapOpzioni] = useState([]);
  const cercaComuneCap = async (testo) => {
    set('localita', testo);
    if (testo.trim().length < 2) { setRisultatiComuneCap([]); return; }
    try {
      const r = await API.get(`/comuni-cap/cerca?q=${encodeURIComponent(testo)}`);
      setRisultatiComuneCap(r.data);
    } catch (e) { setRisultatiComuneCap([]); }
  };
  const selezionaComuneCap = (cm) => {
    setForm(f => ({ ...f, localita: cm.denominazione, provincia: cm.provincia || '',
      cap: cm.cap && cm.cap.length === 1 ? cm.cap[0] : '' }));
    setCapOpzioni(cm.cap || []);
    setRisultatiComuneCap([]);
  };
  const isAzienda = form.tipo_cliente === 'azienda';
  const [convenzioni, setConvenzioni] = useState([]);
  useEffect(() => {
    API.get('/convenzioni').then(r => setConvenzioni(r.data)).catch(() => {});
  }, []);
  const [ricercaComune, setRicercaComune] = useState('');
  const [risultatiComune, setRisultatiComune] = useState([]);
  const [codiceBelfioreNascita, setCodiceBelfioreNascita] = useState(null);
  const [avvisoCf, setAvvisoCf] = useState(null);
  const sincronizzandoDaCf = useRef(false);

  // CF -> data/luogo/sesso: quando il CF cambia ed e' valido, decodifica e riempi
  useEffect(() => {
    const decoded = decodificaCF(form.codice_fiscale);
    if (!decoded) return;
    sincronizzandoDaCf.current = true;
    API.get(`/comuni/codice/${decoded.codiceBelfiore}`).then(r => {
      const anno = annoCompletoDaCF(decoded.anno2);
      const dataStr = `${anno}-${String(decoded.mese).padStart(2,'0')}-${String(decoded.giorno).padStart(2,'0')}`;
      setForm(f => ({ ...f, data_nascita: dataStr, luogo_nascita: r.data.denominazione,
        provincia_nascita: r.data.provincia || '', sesso: decoded.sesso }));
      setCodiceBelfioreNascita(decoded.codiceBelfiore);
      setRicercaComune(r.data.denominazione);
      setAvvisoCf(null);
    }).catch(() => {
      setAvvisoCf('Codice del luogo di nascita non riconosciuto.');
    }).finally(() => { sincronizzandoDaCf.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.codice_fiscale]);

  // data/luogo/sesso (+ cognome/nome) -> genera il CF, se non stiamo sincronizzando dal CF stesso
  useEffect(() => {
    if (sincronizzandoDaCf.current) return;
    if (!form.cognome || !form.nome || !form.sesso || !form.data_nascita || !codiceBelfioreNascita) return;
    const generato = codificaCF(form.cognome, form.nome, form.sesso, new Date(form.data_nascita + 'T00:00:00'), codiceBelfioreNascita);
    if (generato && generato !== form.codice_fiscale) {
      setForm(f => ({ ...f, codice_fiscale: generato }));
      setAvvisoCf('Codice fiscale generato automaticamente: verificarne la correttezza (eventuali omocodie non sono gestite).');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cognome, form.nome, form.sesso, form.data_nascita, codiceBelfioreNascita]);

  const cercaComune = async (testo) => {
    setRicercaComune(testo);
    if (testo.trim().length < 2) { setRisultatiComune([]); return; }
    try {
      const r = await API.get(`/comuni/cerca?q=${encodeURIComponent(testo)}`);
      setRisultatiComune(r.data);
    } catch (e) { setRisultatiComune([]); }
  };
  const selezionaComune = (comune) => {
    setForm(f => ({ ...f, luogo_nascita: comune.denominazione, provincia_nascita: comune.provincia || '' }));
    setCodiceBelfioreNascita(comune.codice_belfiore);
    setRicercaComune(comune.denominazione);
    setRisultatiComune([]);
  };

  const valida = () => {
    const e = {};
    if (!isAzienda && form.codice_fiscale && !validaCF(form.codice_fiscale))
      e.codice_fiscale = 'Codice fiscale non valido';
    if (isAzienda && form.partita_iva && !validaPI(form.partita_iva))
      e.partita_iva = 'Partita IVA non valida';
    setErrori(e);
    return Object.keys(e).length === 0;
  };

  const salva = async () => {
    if (!valida()) return;
    setCarico(true);
    try {
      const cleanForm = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      await API.patch(`/clienti/${cliente.codice_cliente}`, cleanForm);
      await onSave();
    } catch(e) {
      console.error(e);
      setCarico(false);
    }
  };

  return (
    <div style={{ background: '#F7F8FA', border: '1px solid #dde1e7', borderRadius: 10, padding: 20 }}>

      {/* Tipo cliente */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#2E75B6', textTransform: 'uppercase',
          letterSpacing: '0.8px', marginBottom: 10 }}>Tipo cliente</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {['privato','azienda'].map(t => (
            <button key={t} onClick={() => set('tipo_cliente', t)}
              style={{ padding: '6px 20px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
                border: `1px solid ${form.tipo_cliente === t ? '#2E75B6' : '#dde1e7'}`,
                background: form.tipo_cliente === t ? '#EBF4FF' : 'white',
                color: form.tipo_cliente === t ? '#2E75B6' : '#718096',
                fontWeight: form.tipo_cliente === t ? 600 : 400 }}>
              {t === 'privato' ? '👤 Privato' : '🏢 Azienda'}
            </button>
          ))}
        </div>
      </div>

      {/* Dati anagrafici */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#2E75B6', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #2E75B620' }}>
        {isAzienda ? 'Dati azienda' : 'Dati anagrafici'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        {!isAzienda ? (
          <>
            <LabelInput label="Cognome">
              <input className="input-filtro" style={{ width: '100%' }} value={form.cognome}
                onChange={e => set('cognome', e.target.value)} />
            </LabelInput>
            <LabelInput label="Nome">
              <input className="input-filtro" style={{ width: '100%' }} value={form.nome}
                onChange={e => set('nome', e.target.value)} />
            </LabelInput>
            <LabelInput label="Codice fiscale" errore={errori.codice_fiscale}>
              <input className="input-filtro" style={{ width: '100%',
                borderColor: errori.codice_fiscale ? '#C53030' : undefined }}
                value={form.codice_fiscale}
                onChange={e => set('codice_fiscale', e.target.value.toUpperCase())}
                onBlur={() => valida()} maxLength={16} placeholder="RSSMRA80A01F839X" />
              {avvisoCf && <div style={{ fontSize: 11, color: '#8E44AD', marginTop: 4 }}>{avvisoCf}</div>}
            </LabelInput>
            <LabelInput label="Data di nascita">
              <input type="date" className="input-filtro" style={{ width: '100%' }}
                value={form.data_nascita} onChange={e => set('data_nascita', e.target.value)} />
            </LabelInput>
            <LabelInput label="Sesso">
              <select className="input-filtro" style={{ width: '100%' }}
                value={form.sesso} onChange={e => set('sesso', e.target.value)}>
                <option value="">— Seleziona —</option>
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
            </LabelInput>
            <LabelInput label="Luogo di nascita">
              <div style={{ position: 'relative' }}>
                <input className="input-filtro" style={{ width: '100%' }}
                  value={ricercaComune} onChange={e => cercaComune(e.target.value)}
                  placeholder="Cerca comune o stato estero..." autoComplete="off" />
                {risultatiComune.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                    background: 'white', border: '1px solid #dde1e7', borderRadius: 6, marginTop: 2,
                    maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
                    {risultatiComune.map(cm => (
                      <div key={cm.codice_belfiore} onClick={() => selezionaComune(cm)}
                        style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        {cm.denominazione} {cm.provincia ? `(${cm.provincia})` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </LabelInput>
            <LabelInput label="Convenzione">
              <select className="input-filtro" style={{ width: '100%' }}
                value={form.convenzione}
                onChange={e => set('convenzione', e.target.value)}>
                <option value="">— Nessuna —</option>
                {convenzioni.map(cv => (
                  <option key={cv.id} value={cv.nome}>{cv.nome}</option>
                ))}
              </select>
            </LabelInput>
          </>
        ) : (
          <>
            <LabelInput label="Ragione sociale">
              <input className="input-filtro" style={{ width: '100%' }} value={form.ragione_sociale}
                onChange={e => set('ragione_sociale', e.target.value)} />
            </LabelInput>
            <LabelInput label="Partita IVA" errore={errori.partita_iva}>
              <input className="input-filtro" style={{ width: '100%',
                borderColor: errori.partita_iva ? '#C53030' : undefined }}
                value={form.partita_iva}
                onChange={e => set('partita_iva', e.target.value)}
                onBlur={() => valida()} maxLength={11} placeholder="12345678901" />
            </LabelInput>
            <LabelInput label="Pubblica amministrazione">
              <select className="input-filtro" style={{ width: '100%' }} value={form.pubblica_amministrazione}
                onChange={e => set('pubblica_amministrazione', e.target.value === 'true')}>
                <option value="false">No</option>
                <option value="true">Sì</option>
              </select>
            </LabelInput>
            <LabelInput label="Cognome referente">
              <input className="input-filtro" style={{ width: '100%' }} value={form.cognome_referente}
                onChange={e => set('cognome_referente', e.target.value)} />
            </LabelInput>
            <LabelInput label="Nome referente">
              <input className="input-filtro" style={{ width: '100%' }} value={form.nome_referente}
                onChange={e => set('nome_referente', e.target.value)} />
            </LabelInput>
          </>
        )}
      </div>

      {/* Recapiti */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#E67E22', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #E67E2220' }}>
        Recapiti
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Indirizzo">
          <input className="input-filtro" style={{ width: '100%' }} value={form.indirizzo}
            onChange={e => set('indirizzo', e.target.value)} placeholder="Via Roma 10" />
        </LabelInput>
        <LabelInput label="Comune">
          <div style={{ position: 'relative' }}>
            <input className="input-filtro" style={{ width: '100%' }}
              value={form.localita}
              onChange={e => cercaComuneCap(e.target.value)}
              placeholder="Cerca comune..." autoComplete="off" />
            {risultatiComuneCap.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: 'white', border: '1px solid #dde1e7', borderRadius: 6, marginTop: 2,
                maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
                {risultatiComuneCap.map(cm => (
                  <div key={`${cm.denominazione}-${cm.provincia}`} onClick={() => selezionaComuneCap(cm)}
                    style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    {cm.denominazione} {cm.provincia ? `(${cm.provincia})` : ''}
                    {cm.cap && cm.cap.length === 1 ? ` — ${cm.cap[0]}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </LabelInput>
        <LabelInput label="CAP">
          {capOpzioni.length > 1 ? (
            <select className="input-filtro" style={{ width: '100%' }}
              value={form.cap} onChange={e => set('cap', e.target.value)}>
              <option value="">— Seleziona zona —</option>
              {capOpzioni.map(cp => <option key={cp} value={cp}>{cp}</option>)}
            </select>
          ) : (
            <input className="input-filtro" style={{ width: '100%' }} value={form.cap}
              onChange={e => set('cap', e.target.value)} maxLength={5} />
          )}
        </LabelInput>
        <LabelInput label="Provincia">
          <input className="input-filtro" style={{ width: '100%' }} value={form.provincia}
            onChange={e => set('provincia', e.target.value.toUpperCase())} maxLength={2} placeholder="NA" />
        </LabelInput>
        <LabelInput label="Telefono 1">
          <input className="input-filtro" style={{ width: '100%' }} value={form.telefono}
            onChange={e => set('telefono', e.target.value)} />
        </LabelInput>
        <LabelInput label="Telefono 2">
          <input className="input-filtro" style={{ width: '100%' }} value={form.telefono2}
            onChange={e => set('telefono2', e.target.value)} />
        </LabelInput>
        <LabelInput label="Email">
          <input className="input-filtro" style={{ width: '100%' }} value={form.email}
            onChange={e => set('email', e.target.value)} />
        </LabelInput>
        {isAzienda && (
          <LabelInput label="PEC">
            <input className="input-filtro" style={{ width: '100%' }} value={form.pec}
              onChange={e => set('pec', e.target.value)} />
          </LabelInput>
        )}
        {isAzienda && (
          <LabelInput label="Codice SDI">
            <input className="input-filtro" style={{ width: '100%' }} value={form.codice_sdi}
              onChange={e => set('codice_sdi', e.target.value.toUpperCase())} maxLength={7} />
          </LabelInput>
        )}
      </div>

      {/* Note */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #71809620' }}>
        Note
      </div>
      <textarea className="input-filtro" style={{ width: '100%', height: 60, resize: 'vertical', marginBottom: 16 }}
        value={form.note} onChange={e => set('note', e.target.value)} placeholder="Note aggiuntive..." />

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-cerca" onClick={salva} disabled={carico}>
            {carico ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
            Salva anagrafica
          </button>
          <button onClick={onCancel} style={{
            padding: '10px 20px', borderRadius: 8, border: '1px solid #dde1e7',
            background: 'white', cursor: 'pointer', fontSize: 14, color: '#718096'
          }}>Annulla</button>
        </div>
        {onElimina && (
          <button type="button" onClick={async () => {
              const nome = cliente.ragione_sociale || `${cliente.cognome || ''} ${cliente.nome || ''}`.trim();
              if (!window.confirm(`Eliminare definitivamente il cliente ${nome}? Operazione irreversibile.`)) return;
              try {
                await API.delete(`/clienti/${cliente.codice_cliente}`);
                onElimina();
              } catch (e) {
                window.alert(e.response?.data?.detail || 'Errore nell\'eliminazione.');
              }
            }}
            style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #C53030',
              background: 'white', color: '#C53030', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            🗑 Elimina cliente
          </button>
        )}
      </div>
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────
export default function SchedaCliente({ cliente: clienteIniziale, onAggiornamento }) {
  const { haPermesso } = useAuth();
  const [cliente, setCliente] = useState(clienteIniziale);
  const [modifica, setModifica] = useState(false);

  if (!cliente) return null;

  if (modifica) {
    return (
      <FormCliente
        cliente={cliente}
        onSave={async () => {
          const r = await API.get(`/clienti/${cliente.codice_cliente}`);
          setCliente(r.data);
          setModifica(false);
          if (onAggiornamento) onAggiornamento(r.data);
        }}
        onCancel={() => setModifica(false)}
        onElimina={haPermesso('clienti.elimina') ? () => { if (onAggiornamento) onAggiornamento(null); } : undefined}
      />
    );
  }

  return <VistaCliente cliente={cliente} onModifica={() => setModifica(true)} />;
}
